/**
 * Standard request/response shapes for paginated list endpoints.
 *
 * Request:  { page, limit, search?, sortBy?, sortDir? }
 * Response (wire / backend `ResponseData`): { data, total, currentPage, count }
 *
 * `Pagination` + `buildPagination` are UI/helpers (see `DataTable`). `ListResponse`
 * remains for tests and demos that prefer a nested `pagination` object.
 *
 * Feature-specific filters can extend `ListQuery` (e.g. `ListQuery & { status?: string }`).
 */

export type SortDir = 'asc' | 'desc';

export interface ListQuery {
  page: number;
  limit: number;
  search?: string;
  /** Column key to sort by. Servers MUST whitelist allowed values. */
  sortBy?: string;
  /** Sort direction. Defaults to 'asc' when a `sortBy` is provided. */
  sortDir?: SortDir;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface ResponseDataList<T> {
  data: T[];
  total: number;
  currentPage: number;
  count: number;
}

export const DEFAULT_PAGE = 1;
export const DEFAULT_LIMIT = 10;
export const PAGE_SIZE_OPTIONS: readonly number[] = [5, 10, 25, 50, 100];

export function defaultListQuery(overrides: Partial<ListQuery> = {}): ListQuery {
  return {
    page: overrides.page ?? DEFAULT_PAGE,
    limit: overrides.limit ?? DEFAULT_LIMIT,
    ...(overrides.search !== undefined ? { search: overrides.search } : {}),
    ...(overrides.sortBy !== undefined ? { sortBy: overrides.sortBy } : {}),
    ...(overrides.sortDir !== undefined ? { sortDir: overrides.sortDir } : {}),
  };
}

export function buildPagination(page: number, limit: number, total: number): Pagination {
  const safeLimit = Math.max(1, limit);
  const totalPages = Math.max(1, Math.ceil(total / safeLimit));
  return {
    page,
    limit: safeLimit,
    total,
    totalPages,
    hasNext: page < totalPages,
    hasPrev: page > 1,
  };
}

/**
 * Slice a full collection like the real backend would. Used by MSW handlers and tests.
 */
export function paginate<T>(items: readonly T[], query: ListQuery): ResponseDataList<T> {
  const page = Math.max(1, Math.floor(query.page));
  const limit = Math.max(1, Math.floor(query.limit));
  const start = (page - 1) * limit;
  const data = [...items.slice(start, start + limit)];
  return {
    data,
    total: items.length,
    currentPage: page,
    count: data.length,
  };
}

/**
 * Serialize a `ListQuery` into a flat record suitable for `params` in an RTK Query
 * endpoint. Drops `undefined` and empty-string `search` so the server sees a clean URL.
 */
export function listQueryToParams(query: ListQuery): Record<string, string | number> {
  const params: Record<string, string | number> = { page: query.page, limit: query.limit };
  if (query.search && query.search.trim().length > 0) {
    params.term = query.search.trim();
  }
  if (query.sortBy) {
    if (query.sortDir === 'desc') {
      params.dsc = query.sortBy;
    } else {
      params.asc = query.sortBy;
    }
  }
  return params;
}

export type Sortable = string | number | boolean | Date;

/**
 * Primitive comparison that orders numbers numerically, booleans with `false < true`,
 * `Date` objects chronologically, and everything else via locale-aware string compare.
 * Nullish values always sort before defined values regardless of direction.
 */
export function compareValues(
  a: Sortable | null | undefined,
  b: Sortable | null | undefined,
): number {
  const aNil = a === null || a === undefined;
  const bNil = b === null || b === undefined;
  if (aNil && bNil) return 0;
  if (aNil) return -1;
  if (bNil) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return a === b ? 0 : a ? 1 : -1;
  }
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  const sa = a instanceof Date ? a.toISOString() : String(a);
  const sb = b instanceof Date ? b.toISOString() : String(b);
  return sa.localeCompare(sb);
}

export type SortResolver<T> = (row: T) => Sortable | null | undefined;

/**
 * Sort a collection by a whitelisted column key using the provided resolver map.
 * Unknown keys are ignored (returns a stable copy), keeping the signature safe for
 * URL-sourced input in mock backends.
 */
export function sortItems<T>(
  items: readonly T[],
  sortBy: string | undefined,
  sortDir: SortDir = 'asc',
  resolvers: Record<string, SortResolver<T>>,
): T[] {
  if (!sortBy) return [...items];
  const resolver = resolvers[sortBy];
  if (!resolver) return [...items];
  const mul = sortDir === 'desc' ? -1 : 1;
  return [...items].sort((a, b) => compareValues(resolver(a), resolver(b)) * mul);
}
